import { useMutation } from '@apollo/client/react';
import { GET_CONTACT_CUSTOMER_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { DELETE_PHONES_MUTATION } from '../../integrations/allkort/graphql/gql/PhoneMutations';
import { DeletePhonesResponse } from '../../integrations/allkort/graphql/interface/Phones';

/**
 * useDeletePhone
 *
 * Custom hook for deleting a phone number from the customer's account.
 *
 * @example
 * ```tsx
 * const { deletePhone, loading } = useDeletePhone();
 *
 * const handleDelete = async () => {
 *   const result = await deletePhone({ phoneId: 939746 });
 *   if (result) console.log('Deleted phone IDs:', result.phoneIds);
 * };
 * ```
 */
export const useDeletePhone = () => {
	const [deletePhonesMutation, { loading, error, data }] = useMutation<DeletePhonesResponse>(
		DELETE_PHONES_MUTATION
	);

	/**
	 * Delete a phone number from the customer's account
	 * @param phoneId - The ID of the phone number to delete
	 * @returns The phoneNumberDeletePayload with deleted phoneIds, or throws on failure
	 */
	const deletePhone = async ({ phoneId }: { phoneId: number }) => {
		try {
			const result = await deletePhonesMutation({
				variables: { phoneNumberIds: [phoneId] },
				refetchQueries: [{ query: GET_CONTACT_CUSTOMER_DATA_QUERY }],
				awaitRefetchQueries: true,
			});

			if (result.error) {
				const apolloError = result.error as any;
				const message = apolloError.graphQLErrors?.[0]?.message ?? apolloError.message;
				console.error('[useDeletePhone] GraphQL error:', message);
				throw new Error(message);
			}

			const payload = result.data?.deletePhones?.phoneNumberDeletePayload ?? null;
			console.log('[useDeletePhone] Deleted successfully:', payload);
			return payload;
		} catch (err) {
			throw err;
		}
	};

	return { deletePhone, loading, error, data };
};
